/**
 * FileField -- Dynamic file upload field.
 *
 * Renders a file upload area using react-dropzone (already a project
 * dependency). Supports `accepted_extensions` from the field schema to
 * restrict selectable file types. Displays the selected filename after
 * upload. Includes label, required indicator, help text, tooltip,
 * validation error display, and full ARIA accessibility.
 */

import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { HelpCircle, Upload, File as FileIcon, X } from 'lucide-react';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { DynamicFieldProps } from './index';

/**
 * Build a react-dropzone `accept` map from an array of file extensions.
 *
 * react-dropzone expects an object like `{ 'application/json': ['.json'] }`.
 * Since connector schemas define raw extensions (e.g. `[".json", ".csv"]`)
 * and we do not always know the exact MIME type, we use a wildcard MIME and
 * let the extension filter do the work.
 */
function extensionsToAccept(
  extensions: string[] | undefined
): Record<string, string[]> | undefined {
  if (!extensions || extensions.length === 0) return undefined;

  // Normalise: ensure each extension starts with a dot
  const normalised = extensions.map((ext) =>
    ext.startsWith('.') ? ext : `.${ext}`
  );

  // Group under a wildcard MIME -- dropzone uses the extension list for
  // actual filtering, so the MIME key is cosmetic here.
  return { 'application/*': normalised };
}

export function FileField({ field, value, onChange, error, readOnly }: DynamicFieldProps) {
  const fieldId = `field-${field.name}`;
  const helpId = `${fieldId}-help`;
  const errorId = `${fieldId}-error`;
  const formatsId = `${fieldId}-formats`;

  const [fileName, setFileName] = React.useState<string | null>(
    typeof value === 'string' && value.length > 0 ? 'Uploaded file' : null
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        setFileName(file.name);
        onChange(reader.result as string);
      };
      reader.onerror = () => {
        setFileName(null);
        onChange(null);
      };
      reader.readAsText(file);
    },
    [onChange]
  );

  const accept = extensionsToAccept(field.accepted_extensions);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple: false,
    disabled: readOnly,
  });

  const describedBy = [
    field.accepted_extensions?.length ? formatsId : null,
    field.help_text ? helpId : null,
    error ? errorId : null,
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFileName(null);
    onChange(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <Label id={`${fieldId}-label`}>
          {field.label}
          {field.required && (
            <>
              <span aria-hidden="true" className="text-destructive ml-1">*</span>
              <span className="sr-only">(required)</span>
            </>
          )}
        </Label>

        {field.tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle
                  className="h-3.5 w-3.5 text-muted-foreground cursor-help"
                  aria-hidden="true"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-sm">{field.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <div
        {...getRootProps()}
        role="button"
        tabIndex={readOnly ? -1 : 0}
        aria-label={`Upload ${field.label}`}
        aria-required={field.required}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-6 text-sm transition-colors cursor-pointer',
          isDragActive && 'border-primary bg-primary/5',
          error && 'border-destructive',
          readOnly && 'cursor-not-allowed opacity-50',
          !isDragActive && !error && 'border-input hover:border-primary/50'
        )}
      >
        <input {...getInputProps()} id={fieldId} />

        {fileName ? (
          <div className="flex items-center gap-2">
            <FileIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <span className="font-medium">{fileName}</span>
            {!readOnly && (
              <button
                type="button"
                onClick={handleClear}
                className="rounded-full p-0.5 hover:bg-muted"
                aria-label={`Remove ${fileName}`}
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            )}
          </div>
        ) : (
          <>
            <Upload className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
            <p className="text-muted-foreground">
              {isDragActive
                ? 'Drop the file here'
                : 'Drag and drop a file, or click to browse'}
            </p>
          </>
        )}
      </div>

      {field.accepted_extensions && field.accepted_extensions.length > 0 && (
        <p id={formatsId} className="text-xs text-muted-foreground">
          Accepted formats: {field.accepted_extensions.join(', ')}
        </p>
      )}

      {field.help_text && (
        <p id={helpId} className="text-sm text-muted-foreground">
          {field.help_text}
        </p>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
